import styled, { css } from 'styled-components';

import { size } from '../../layout/helpers';

const buttonMinHeight = 6;
const buttonSmallMinHeight = 4.5;

export type ButtonNameTypes = 'primary' | 'secondary' | 'accent' | 'clear';

interface ButtonProps {
  $tone?: ButtonNameTypes;
  $size?: 'small';
  $fullWidth?: boolean | 'mobile' | 'desktop';
  $inline?: boolean;
  $shadow?: boolean;
}

export const Button = styled.button.attrs(() => ({
  type: 'button',
})).withConfig({ componentId: 'sc-81ou2v-0' })<ButtonProps>`
    --button-bg: var(--c-bg-main);
    --button-fg: var(--c-text-main);

    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${size(1)};
    width: max-content;
    min-height: ${size(buttonMinHeight)};
    padding: ${size(1.5)} ${size(4)};
    margin: 0;
    background-color: var(--button-bg);
    color: var(--button-fg);
    font-family: var(--font-family);
    font-weight: var(--font-weight-medium);
    border: 0;
    border-radius: 100vmax;
    -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
    font-size: var(--font-size-small-static);
    line-height: ${(props) => props.theme.font.lineHeight.thin};
    text-align: center;
    text-decoration: none;
    transform-origin: 50% 50%;
    transition: transform 200ms ease;
    user-select: none;
    cursor: pointer;

    &::before {
        content: "";
        position: absolute;
        inset: 1px;
        background-color: var(--c-overlay);
        border-radius: inherit;
        mix-blend-mode: soft-light;
        opacity: 0;
        transition: opacity 200ms ease;
    }

    &:active {
        transform: scale(0.97);
    }

    &:not([disabled]):hover {
        &::before {
            opacity: 1;
        }
    }

    &[disabled] {
        opacity: 0.37;
        cursor: not-allowed;
    }

    ${({ $tone }) =>
      $tone === 'primary'
        ? css`
                  --button-bg: var(--c-primary-bg);
                  --button-fg: var(--c-primary-fg);
              `
        : $tone === 'secondary'
          ? css`
                  --button-bg: var(--c-secondary-bg);
                  --button-fg: var(--c-secondary-fg);
              `
          : $tone === 'accent'
            ? css`
                  --button-bg: var(--c-accent-bg);
                  --button-fg: var(--c-accent-fg);
              `
            : $tone === 'clear'
              ? css`
                  --button-bg: transparent;
                  --button-fg: var(--c-primary-bg);

                  // Disable hover overlay due to bug with mix-blend-mode and transparent background
                  &::before {
                      content: none;
                  }
              `
              : css`
                  // Disable hover overlay due to bug with mix-blend-mode and transparent background
                  &::before {
                      content: none;
                  }
              `}

    ${({ $size }) =>
      $size === 'small'
        ? css`
                  min-height: ${size(buttonSmallMinHeight)};
                  padding: ${size(1)} ${size(2)};
              `
        : undefined}

  ${({ $fullWidth, theme }) =>
    $fullWidth && typeof $fullWidth === 'boolean'
      ? css`
                  width: 100%;
              `
      : $fullWidth === 'mobile'
        ? css`
                  ${theme.breakpoint.LtMd} {
                      width: 100%;
                  }
              `
        : $fullWidth === 'desktop'
          ? css`
                  ${theme.breakpoint.Md} {
                      width: 100%;
                  }
              `
          : null}

  ${({ $inline }) =>
    $inline &&
    css`
            display: inline-flex;
        `}

  ${({ $shadow }) =>
    $shadow &&
    css`
            box-shadow: var(--drop-shadow);
        `}
`;
